import _defineProperty from "/app/node_modules/.pnpm/next@12.3.3_@babel+core@7.14.3_react-dom@18.2.0_react@18.2.0_sass@1.49.9/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Cell, Divider, Image, Rating, Spacer, Text } from '@vinted/web-ui';
import useTracking from 'hooks/useTracking';
import useTranslate from 'hooks/useTranslate';
import { SECONDS_PER_DAY } from 'constants/date';
import { STATIC_ASSETS_URL } from 'constants/index';
import { ClickableElement } from 'constants/tracking/clickable-elements';
import { Screen } from 'constants/tracking/screens';
import { abTestExposeEvent, clickEvent } from 'libs/common/event-tracker/events';
import { getCookie, saveCookie } from 'libs/utils/cookies';
import { relativeDate } from 'libs/utils/date';
import { serverSide } from 'libs/utils/environment';
import * as device from 'libs/utils/userAgentParser';
import { getUserAgent } from 'libs/utils/window';
import { getScreenName } from 'state/screen/selectors';
import { getExposee, getUserId } from 'state/session/selectors';
import { getAbTestByName } from 'state/ab-tests/selectors'; // It is decided to have hardcoded app rating with no need to sync it with store

import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const APP_RATING = 4.5;
const APP_ICON = "".concat(STATIC_ASSETS_URL, "/assets/logo/square/transparent.png");
const APP_URL = 'https://4uwb.adj.st/?adjust_t=l2fpdc8_rnxf4nj&adjust_campaign=UX_SmartBanner&adjust_engagement_type=fallback_click';
const COOKIE_NAME = 'app_banner';
const COOKIE_EXPIRATION_TIME = SECONDS_PER_DAY * 5;
const LATEST_ALLOWED_ANDROID_VERSION = 7;
const appBannerScreens = [Screen.NewsFeed, Screen.UserProfile, Screen.ForumPosts, Screen.ForumTopic, Screen.Catalog];

const AppBanner = _ref => {
  let {
    userAgent = getUserAgent(),
    isHeader
  } = _ref;
  const {
    track
  } = useTracking();
  const translate = useTranslate('app_banner');
  const userId = useSelector(getUserId);
  const screenName = useSelector(getScreenName);
  const webToAppAbTest = useSelector(getAbTestByName('web_2_app_conversion_v1'));
  const exposee = useSelector(getExposee);

  const getAndroidVersion = () => {
    const match = userAgent.toLowerCase().match(/android\s([0-9.]*)/i);
    return match ? parseInt(match[1], 10) : LATEST_ALLOWED_ANDROID_VERSION;
  };

  const shouldNotRenderOnMobile = () => !device.isIOS(userAgent) && !(device.isAndroid(userAgent) && getAndroidVersion() >= LATEST_ALLOWED_ANDROID_VERSION);

  const {
    0: isHidden,
    1: setIsHidden
  } = useState(false);
  useEffect(() => {
    if (!webToAppAbTest) return;
    track(abTestExposeEvent(_objectSpread(_objectSpread({}, exposee), webToAppAbTest)));
  }, [webToAppAbTest, exposee, track]);
  if (serverSide) return null;
  if (isHidden) return null;
  if (userId) return null;
  if (getCookie(COOKIE_NAME)) return null;
  if (shouldNotRenderOnMobile()) return null;
  if (!appBannerScreens.includes(screenName)) return null;
  if ((webToAppAbTest === null || webToAppAbTest === void 0 ? void 0 : webToAppAbTest.variant) === 'on' && !isHeader && screenName === Screen.NewsFeed) return null;

  function trackClick(target) {
    track(clickEvent({
      target,
      targetDetails: device.parseOS(userAgent)
    }));
  }

  function handleOpenClick() {
    trackClick(ClickableElement.InstallAppBanner);
  }

  function handleCloseClick() {
    setIsHidden(true);
    trackClick(ClickableElement.CancelInstallAppBanner);
    saveCookie(COOKIE_NAME, 'hidden', relativeDate({
      seconds: COOKIE_EXPIRATION_TIME
    }));
  }

  function renderActions() {
    return /*#__PURE__*/_jsxs("div", {
      className: "u-flexbox",
      children: [/*#__PURE__*/_jsx(Button, {
        size: Button.Size.Medium,
        text: translate('close_cta'),
        onClick: handleCloseClick,
        testId: "app-banner-close-button"
      }), /*#__PURE__*/_jsx(Spacer, {
        orientation: Spacer.Orientation.Vertical
      }), /*#__PURE__*/_jsx(Button, {
        theme: "primary",
        styling: Button.Styling.Filled,
        size: Button.Size.Medium,
        text: translate('cta'),
        onClick: handleOpenClick,
        url: APP_URL,
        testId: "app-banner-open-button"
      })]
    });
  }

  function renderTabletActions() {
    return /*#__PURE__*/_jsx("div", {
      className: "u-tablets-only",
      children: renderActions()
    });
  }

  function renderPhoneActions() {
    return /*#__PURE__*/_jsxs("div", {
      className: "u-phones-only",
      children: [/*#__PURE__*/_jsx(Spacer, {
        size: Spacer.Size.Large
      }), /*#__PURE__*/_jsx(Cell, {
        styling: Cell.Styling.Tight,
        children: renderActions()
      })]
    });
  }

  function renderTitle() {
    return /*#__PURE__*/_jsxs("div", {
      className: "u-flexbox",
      children: [/*#__PURE__*/_jsx(Text, {
        type: Text.Type.Title,
        text: translate('title'),
        truncate: true
      }), /*#__PURE__*/_jsx(Spacer, {
        orientation: Spacer.Orientation.Vertical,
        size: Spacer.Size.Small
      }), /*#__PURE__*/_jsxs("div", {
        children: [/*#__PURE__*/_jsx(Spacer, {
          size: Spacer.Size.XSmall
        }), /*#__PURE__*/_jsx(Rating, {
          value: APP_RATING
        })]
      })]
    });
  }

  return /*#__PURE__*/_jsx("div", {
    className: "app-banner",
    children: /*#__PURE__*/_jsx("div", {
      className: isHeader && (webToAppAbTest === null || webToAppAbTest === void 0 ? void 0 : webToAppAbTest.variant) === 'on' ? 'sticky-header' : 'sticky-footer',
      "data-testid": "app-banner-wrapper",
      children: /*#__PURE__*/_jsxs("div", {
        className: "u-line-height-default",
        children: [!isHeader && /*#__PURE__*/_jsx(Divider, {}), /*#__PURE__*/_jsxs(Cell, {
          children: [/*#__PURE__*/_jsx(Cell, {
            styling: Cell.Styling.Tight,
            prefix: /*#__PURE__*/_jsx(Image, {
              size: Image.Size.Large,
              src: APP_ICON
            }),
            suffix: renderTabletActions(),
            title: renderTitle(),
            body: /*#__PURE__*/_jsx(Text, {
              text: translate('description'),
              truncate: true
            }),
            fullWidthTitle: true
          }), renderPhoneActions()]
        }), isHeader && /*#__PURE__*/_jsx(Divider, {})]
      })
    })
  });
};

export default AppBanner;